import { ref, watch, onBeforeUnmount } from 'vue'

/*
 * Global "/" keyboard shortcut → focus the page's primary search input.
 *
 * Architecture:
 *   - One module-level reactive counter (`focusRequested`) that gets bumped
 *     whenever the user presses "/" outside of an input/textarea/contenteditable.
 *   - Components call `useSearchShortcutTarget(onFocus)` to react to
 *     subsequent presses. Subscribers all run their callback on every press.
 */

const focusRequested = ref(0)
let installed = false

function isEditable(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false
    const tag = target.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
    if (target.isContentEditable) return true
    return false
}

function onKeydown(e: KeyboardEvent) {
    if (e.key !== '/') return
    if (isEditable(e.target)) return
    if (e.metaKey || e.ctrlKey || e.altKey) return
    e.preventDefault()
    focusRequested.value++
}

/**
 * Mount once at the app root (App.vue) to install the global "/" listener.
 * Idempotent — safe to call multiple times.
 */
export function installSearchShortcut() {
    if (installed) return
    installed = true
    if (typeof document !== 'undefined') {
        document.addEventListener('keydown', onKeydown)
    }
}

/**
 * Subscribe to "/" presses. The callback runs each time, automatically
 * unbinds on the calling component's unmount.
 */
export function useSearchShortcutTarget(onFocus: () => void) {
    const stop = watch(focusRequested, () => onFocus())
    onBeforeUnmount(stop)
}
